/**
 * ResumePreviewPanel component wraps ResumePreview with:
 * - Header with template selector
 * - Scrollable preview area
 * - Footer with Generate button
 */

import { cn } from '@/lib/utils'
import type { ResumeContent, TemplateId } from '@/types'
import { FileDown, FileText, Loader2, X } from 'lucide-react'
import { ResumePreview } from './resume-preview'
import { Button } from './ui/button'

interface ResumePreviewPanelProps {
  content: ResumeContent | null
  originalContent?: ResumeContent | null // From parsed_resume_data
  template: TemplateId
  onTemplateChange: (template: TemplateId) => void
  onGenerate: () => void
  onClose?: () => void
  isGenerating: boolean
  isUpdatingContent?: boolean // Shows overlay while AI is generating new content
  jobTitle?: string | undefined // Show if resume is tailored for a specific job
  company?: string | undefined
  className?: string
}

const templateOptions = [
  { value: 'modern', label: 'Modern' },
  // { value: 'pikachu', label: 'Two-Column' }, // Hidden until styling is polished
  { value: 'executive', label: 'Executive' },
] as const

export function ResumePreviewPanel({
  content,
  originalContent,
  template,
  onTemplateChange,
  onGenerate,
  onClose,
  isGenerating,
  isUpdatingContent,
  jobTitle,
  company,
  className,
}: ResumePreviewPanelProps) {
  // Show the current content, or fall back to original content, or null
  const displayContent = content || originalContent

  return (
    <div className={cn('h-full flex flex-col bg-gray-50 relative', className)}>
      {/* Overlay while AI is updating content */}
      {isUpdatingContent && (
        <div className="absolute inset-0 z-10 overflow-hidden">
          {/* Frosted glass overlay */}
          <div className="absolute inset-0 bg-white/80 backdrop-blur-[2px]" />

          {/* Scanning line effect */}
          <div
            className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-indigo-400 to-transparent opacity-60"
            style={{ animation: 'scan 2s ease-in-out infinite' }}
          />

          {/* Center indicator */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              {/* Pulsing rings */}
              <div
                className="absolute inset-0 rounded-full border-2 border-indigo-400/30"
                style={{
                  width: '80px',
                  height: '80px',
                  left: '-20px',
                  top: '-20px',
                  animation: 'pulse-ring 1.5s ease-out infinite',
                }}
              />
              <div
                className="absolute inset-0 rounded-full border-2 border-indigo-400/20"
                style={{
                  width: '80px',
                  height: '80px',
                  left: '-20px',
                  top: '-20px',
                  animation: 'pulse-ring 1.5s ease-out infinite 0.5s',
                }}
              />
              {/* Center icon */}
              <div className="w-10 h-10 rounded-full bg-indigo-500 flex items-center justify-center shadow-lg shadow-indigo-500/30">
                <svg
                  className="w-5 h-5 text-white"
                  style={{ animation: 'spin 2s linear infinite' }}
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <style>{`
            @keyframes scan {
              0% { top: 0%; opacity: 0; }
              10% { opacity: 0.6; }
              90% { opacity: 0.6; }
              100% { top: 100%; opacity: 0; }
            }
            @keyframes pulse-ring {
              0% { transform: scale(0.8); opacity: 0.8; }
              100% { transform: scale(1.5); opacity: 0; }
            }
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}

      {/* Header with template selector */}
      <div className="p-4 border-b bg-white flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-gray-500" />
          <h3 className="font-medium text-gray-900">Resume Preview</h3>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={template}
            onChange={(e) => onTemplateChange(e.target.value as TemplateId)}
            className="w-32 px-3 py-1.5 text-sm border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {templateOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
              title="Close preview"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          )}
        </div>
      </div>

      {/* Job context badge (if tailored for a specific job) */}
      {(jobTitle || company) && (
        <div className="px-4 py-2 bg-indigo-50 border-b border-indigo-100 text-sm">
          <span className="text-indigo-700">
            Tailored for: <strong>{jobTitle}</strong>
            {company && ` at ${company}`}
          </span>
        </div>
      )}

      {/* Scrollable preview area */}
      <div className="flex-1 overflow-y-auto">
        {displayContent ? (
          <div className="p-4">
            <div className="bg-white shadow-sm border rounded-lg overflow-hidden">
              <ResumePreview content={displayContent} template={template} />
            </div>
            {!content && originalContent && (
              <p className="text-center text-sm text-gray-500 mt-4">
                This is your original resume. Ask the AI to improve it!
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <FileText className="h-12 w-12 text-gray-300 mb-4" />
            <p className="text-gray-500">Upload a resume or ask the AI to improve it</p>
            <p className="text-sm text-gray-400 mt-2">Your resume will appear here for preview</p>
          </div>
        )}
      </div>

      {/* Footer with Generate buttons */}
      <div className="p-4 border-t bg-white shrink-0">
        <Button onClick={onGenerate} disabled={!displayContent || isGenerating} className="w-full">
          {isGenerating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <FileDown className="mr-2 h-4 w-4" />
              Generate PDF
            </>
          )}
        </Button>
        {displayContent && (
          <p className="text-xs text-gray-500 text-center mt-2">
            Your download will closely match this preview
          </p>
        )}
      </div>
    </div>
  )
}
